import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarMonthIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CloudDone as CloudDoneIcon,
  ErrorOutline as ErrorOutlineIcon,
  FitnessCenter as FitnessCenterIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  getSetKey,
  TrainingExercise,
  TrainingPlan,
  TrainingSetField,
  trainingPlans,
} from "../data/training";

const API_URL = "/api/training.php";
const LOGIN_API_URL = "/api/login.php";
const LOGOUT_API_URL = "/api/logout.php";
const ME_API_URL = "/api/me.php";
const NOTES_API_URL = "/api/training-notes.php";
const oneDayMs = 24 * 60 * 60 * 1000;

type Entry = {
  value: string;
  updatedAt?: string;
};

type SaveState = "saving" | "saved" | "error";

type PreviousSetSnapshot = {
  setNumber: number;
  weight?: string;
  reps?: string;
};

type PreviousExerciseSession = {
  weekStart: Date;
  dayTitle: string;
  sets: PreviousSetSnapshot[];
};

const Training: React.FC = () => {
  const [activePlanIndex, setActivePlanIndex] = useState(0);
  const [activeDayIndex, setActiveDayIndex] = useState(0);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [saveStates, setSaveStates] = useState<Record<string, SaveState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [driver, setDriver] = useState<string>("unknown");
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [loginUsername, setLoginUsername] = useState("wahab");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [globalNote, setGlobalNote] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteSaveState, setNoteSaveState] = useState<SaveState | null>(null);

  const timers = useRef<Record<string, number>>({});
  const versions = useRef<Record<string, number>>({});

  const activePlan = trainingPlans[activePlanIndex];
  const trainingDays = activePlan.days;
  const activeDay = trainingDays[activeDayIndex];
  const weekId = formatDateId(weekStart);
  const maxSets = Math.max(...activeDay.exercises.map((exercise) => exercise.sets));

  const planSetKeys = useMemo(
    () =>
      trainingDays.flatMap((day) =>
        day.exercises.flatMap((exercise) =>
          Array.from({ length: exercise.sets }).map((_, setIndex) =>
            getSetKey(weekId, day.id, exercise.id, setIndex + 1)
          )
        )
      ),
    [weekId, trainingDays]
  );

  const totalSets = planSetKeys.length;
  const filledSets = useMemo(
    () => planSetKeys.filter((key) => entries[key]?.value !== "").length,
    [entries, planSetKeys]
  );
  const progress = totalSets === 0 ? 0 : Math.round((filledSets / totalSets) * 100);
  const previousExerciseSessions = useMemo(
    () => buildPreviousExerciseSessions(entries, weekStart, activeDay.id, activePlan),
    [entries, weekStart, activeDay.id, activePlan]
  );

  const handlePlanChange = (planIndex: number) => {
    setActivePlanIndex(planIndex);
    setActiveDayIndex(0);
  };

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_URL, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });

      if (response.status === 401) {
        setAuthenticated(false);
        setEntries({});
        return;
      }

      if (!response.ok) {
        throw new Error(`Training API returned ${response.status}`);
      }

      const data = await response.json();
      const nextEntries: Record<string, Entry> = {};

      Object.entries(data.entries ?? {}).forEach(([key, rawEntry]) => {
        if (typeof rawEntry === "string") {
          nextEntries[key] = { value: rawEntry };
          return;
        }

        if (rawEntry && typeof rawEntry === "object") {
          const entry = rawEntry as { value?: unknown; updatedAt?: unknown };
          nextEntries[key] = {
            value: String(entry.value ?? ""),
            updatedAt:
              typeof entry.updatedAt === "string" ? entry.updatedAt : undefined,
          };
        }
      });

      setEntries(nextEntries);
      setDriver(String(data.driver ?? "unknown"));
      setLastSync(new Date());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Could not load data");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGlobalNote = useCallback(async () => {
    setNoteLoading(true);
    try {
      const response = await fetch(NOTES_API_URL, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok) {
        throw new Error(`Notes API returned ${response.status}`);
      }
      const data = await response.json();
      setGlobalNote(typeof data.note === "string" ? data.note : "");
      setNoteSaveState(null);
    } catch {
      setNoteSaveState("error");
    } finally {
      setNoteLoading(false);
    }
  }, []);

  const saveGlobalNote = async () => {
    setNoteSaveState("saving");
    try {
      const response = await fetch(NOTES_API_URL, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ note: globalNote }),
      });
      if (!response.ok) {
        throw new Error(`Notes save returned ${response.status}`);
      }
      setNoteSaveState("saved");
    } catch {
      setNoteSaveState("error");
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(ME_API_URL, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        });
        const data = await response.json().catch(() => ({}));
        setAuthenticated(response.ok && data.authenticated === true);
      } catch {
        setAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    };

    checkAuth();
    const pendingTimers = timers.current;

    return () => {
      Object.values(pendingTimers).forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  useEffect(() => {
    if (authChecked && authenticated) {
      loadEntries();
      loadGlobalNote();
    }
  }, [authChecked, authenticated, loadEntries, loadGlobalNote]);

  const saveCell = useCallback(
    async (cellKey: string, value: string, version: number) => {
      try {
        const response = await fetch(API_URL, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify({ cellKey, value }),
        });

        if (response.status === 401) {
          setAuthenticated(false);
          throw new Error("Authentication required");
        }

        if (!response.ok) {
          throw new Error(`Save failed with ${response.status}`);
        }

        const data = await response.json();

        if (versions.current[cellKey] !== version) {
          return;
        }

        setEntries((current) => ({
          ...current,
          [cellKey]: {
            value,
            updatedAt:
              typeof data.entry?.updatedAt === "string"
                ? data.entry.updatedAt
                : new Date().toISOString(),
          },
        }));
        setDriver(String(data.driver ?? driver));
        setLastSync(new Date());
        setSaveStates((current) => ({ ...current, [cellKey]: "saved" }));

        window.setTimeout(() => {
          setSaveStates((current) => {
            if (current[cellKey] !== "saved") {
              return current;
            }

            const next = { ...current };
            delete next[cellKey];
            return next;
          });
        }, 1200);
      } catch {
        if (versions.current[cellKey] !== version) {
          return;
        }

        setSaveStates((current) => ({ ...current, [cellKey]: "error" }));
      }
    },
    [driver]
  );

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoginLoading(true);
    setLoginError(null);

    try {
      const response = await fetch(LOGIN_API_URL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          username: loginUsername,
          password: loginPassword,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.authenticated !== true) {
        throw new Error(data.error || "Invalid username or password");
      }

      setAuthenticated(true);
      setLoginPassword("");
    } catch (loginFailure) {
      setLoginError(
        loginFailure instanceof Error
          ? loginFailure.message
          : "Could not log in"
      );
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    await fetch(LOGOUT_API_URL, {
      method: "POST",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    }).catch(() => undefined);

    setAuthenticated(false);
    setEntries({});
    setLastSync(null);
  };

  const handleCellChange = (
    cellKey: string,
    rawValue: string,
    field: TrainingSetField
  ) => {
    const value = sanitizeSetValue(rawValue, field);
    const version = (versions.current[cellKey] ?? 0) + 1;
    versions.current[cellKey] = version;

    setEntries((current) => ({
      ...current,
      [cellKey]: {
        ...current[cellKey],
        value,
      },
    }));
    setSaveStates((current) => ({ ...current, [cellKey]: "saving" }));

    if (timers.current[cellKey]) {
      window.clearTimeout(timers.current[cellKey]);
    }

    timers.current[cellKey] = window.setTimeout(() => {
      saveCell(cellKey, value, version);
    }, 300);
  };

  const changeWeek = (offset: number) => {
    setWeekStart((current) => addDays(current, offset * 7));
  };

  const handleWeekDateChange = (value: string) => {
    if (!value) {
      return;
    }

    const [year, month, day] = value.split("-").map(Number);
    setWeekStart(startOfWeek(new Date(year, month - 1, day)));
  };

  if (!authChecked) {
    return <TrainingShell>{renderLoadingPanel()}</TrainingShell>;
  }

  if (!authenticated) {
    return (
      <TrainingShell>
        <LoginPanel
          username={loginUsername}
          password={loginPassword}
          loading={loginLoading}
          error={loginError}
          onUsernameChange={setLoginUsername}
          onPasswordChange={setLoginPassword}
          onSubmit={handleLogin}
        />
      </TrainingShell>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #0f0f23 0%, #1a1a2e 25%, #16213e 50%, #1a1a2e 75%, #0f0f23 100%)",
        color: "white",
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 2, md: 3 } }}>
        <Button
          component={Link}
          to="/"
          startIcon={<ArrowBackIcon />}
          sx={{
            color: "white",
            border: "1px solid rgba(99, 102, 241, 0.35)",
            bgcolor: "rgba(15, 23, 42, 0.35)",
            mb: { xs: 2, md: 3 },
            "&:hover": {
              bgcolor: "rgba(99, 102, 241, 0.12)",
              borderColor: "rgba(168, 85, 247, 0.65)",
            },
          }}
        >
          back
        </Button>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 1fr) 320px" },
            gap: { xs: 2, lg: 3 },
            alignItems: "start",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <Stack direction="row" spacing={1.25} sx={{ alignItems: "center", mb: 1 }}>
                  <FitnessCenterIcon sx={{ color: "#c084fc" }} />
                  <Typography
                    component="h1"
                    sx={{
                      background:
                        "linear-gradient(45deg, #6366f1 20%, #8b5cf6 42%, #a855f7 68%, #c084fc 88%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontSize: { xs: "2.25rem", md: "3rem" },
                      lineHeight: 1.05,
                      fontWeight: 800,
                      letterSpacing: 0,
                      filter: "drop-shadow(0 0 20px rgba(139, 92, 246, 0.28))",
                    }}
                  >
                    training
                  </Typography>
                </Stack>
                <Typography sx={{ color: "rgba(255, 255, 255, 0.72)", fontSize: "1rem" }}>
                  {weekLabel(weekStart)} · {filledSets}/{totalSets} sets
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
              >
                <Chip
                  icon={<CloudDoneIcon />}
                  label={driver}
                  sx={{
                    bgcolor:
                      driver === "mysql"
                        ? "rgba(34, 197, 94, 0.16)"
                        : "rgba(251, 191, 36, 0.16)",
                    border: "1px solid rgba(255, 255, 255, 0.16)",
                    color: "white",
                    fontWeight: 700,
                  }}
                />
                <Tooltip title="Reload saved reps">
                  <span>
                    <IconButton
                      onClick={loadEntries}
                      disabled={loading}
                      sx={iconButtonSx}
                    >
                      {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  onClick={handleLogout}
                  sx={{
                    color: "white",
                    border: "1px solid rgba(255, 255, 255, 0.14)",
                    bgcolor: "rgba(255, 255, 255, 0.05)",
                    px: 2,
                    py: 1,
                    "&:hover": { bgcolor: "rgba(139, 92, 246, 0.18)" },
                  }}
                >
                  logout
                </Button>
              </Stack>
            </Box>

            <Paper elevation={0} sx={panelSx}>
              {trainingPlans.length > 1 && (
                <Box sx={{ px: { xs: 1, md: 2 }, pt: 1.25 }}>
                  <Tabs
                    value={activePlanIndex}
                    onChange={(_, value: number) => handlePlanChange(value)}
                    aria-label="Training split"
                    sx={{
                      minHeight: 40,
                      "& .MuiTab-root": {
                        minHeight: 40,
                        color: "rgba(255, 255, 255, 0.6)",
                        fontWeight: 900,
                        textTransform: "none",
                      },
                      "& .Mui-selected": { color: "#c084fc" },
                      "& .MuiTabs-indicator": { bgcolor: "#a855f7" },
                    }}
                  >
                    {trainingPlans.map((plan) => (
                      <Tab key={plan.id} label={plan.title} />
                    ))}
                  </Tabs>
                </Box>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "auto minmax(0, 1fr) auto",
                    sm: "auto minmax(220px, 1fr) auto minmax(190px, auto)",
                  },
                  gap: 1,
                  alignItems: "center",
                  p: { xs: 1, sm: 1.5 },
                  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <IconButton onClick={() => changeWeek(-1)} sx={iconButtonSx}>
                  <ChevronLeftIcon />
                </IconButton>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <CalendarMonthIcon sx={{ color: "#c084fc", fontSize: 20 }} />
                    <Typography sx={{ color: "white", fontWeight: 800, fontSize: "0.98rem" }}>
                      {weekLabel(weekStart)}
                    </Typography>
                  </Stack>
                  <Typography sx={{ color: "rgba(255, 255, 255, 0.58)", fontSize: "0.78rem" }}>
                    Week {isoWeekNumber(weekStart)}
                  </Typography>
                </Box>
                <IconButton onClick={() => changeWeek(1)} sx={iconButtonSx}>
                  <ChevronRightIcon />
                </IconButton>
                <TextField
                  type="date"
                  value={formatDateId(weekStart)}
                  onChange={(event) => handleWeekDateChange(event.target.value)}
                  size="small"
                  sx={{
                    display: { xs: "none", sm: "block" },
                    width: 190,
                    minWidth: 190,
                    justifySelf: "end",
                    mr: 0.5,
                    ...darkFieldSx,
                  }}
                />
              </Box>

              <Box sx={{ px: { xs: 0.5, md: 2 }, pt: 0.75 }}>
                <Tabs
                  value={activeDayIndex}
                  onChange={(_, value: number) => setActiveDayIndex(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 44,
                    "& .MuiTab-root": {
                      color: "rgba(255, 255, 255, 0.6)",
                      minHeight: 44,
                      fontWeight: 800,
                      textTransform: "none",
                      px: { xs: 1.2, sm: 2 },
                    },
                    "& .Mui-selected": { color: "#c084fc" },
                    "& .MuiTabs-indicator": { bgcolor: "#a855f7" },
                  }}
                >
                  {trainingDays.map((day) => (
                    <Tab key={day.id} label={day.title} />
                  ))}
                </Tabs>
              </Box>

              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 4,
                  bgcolor: "rgba(255, 255, 255, 0.08)",
                  "& .MuiLinearProgress-bar": {
                    background: "linear-gradient(90deg, #6366f1 0%, #a855f7 100%)",
                  },
                }}
              />

              {error && (
                <Alert severity="error" sx={{ borderRadius: 0 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ p: { xs: 1, md: 2 } }}>
                <Typography
                  component="h2"
                  sx={{
                    fontSize: { xs: "1.15rem", md: "1.35rem" },
                    fontWeight: 800,
                    mb: 1.5,
                    color: "white",
                  }}
                >
                  {activeDay.title}
                </Typography>

                <Box sx={{ display: { xs: "block", lg: "none" } }}>
                  <Stack spacing={1.25}>
                    {activeDay.exercises.map((exercise) => (
                      <ExerciseCard
                        key={exercise.id}
                        exercise={exercise}
                        compact={activePlan.compact}
                        weekId={weekId}
                        dayId={activeDay.id}
                        previousSession={previousExerciseSessions[exercise.id]}
                        entries={entries}
                        saveStates={saveStates}
                        onCellChange={handleCellChange}
                      />
                    ))}
                  </Stack>
                </Box>

                <TableContainer
                  sx={{
                    display: { xs: "none", lg: "block" },
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: 1,
                    overflow: "visible",
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      width: "100%",
                      tableLayout: "fixed",
                    }}
                  >
                    <TableHead>
                      <TableRow sx={{ bgcolor: "rgba(99, 102, 241, 0.18)" }}>
                        {!activePlan.compact && <HeaderCell width={54}>#</HeaderCell>}
                        <HeaderCell>Exercise</HeaderCell>
                        <HeaderCell width={220}>Last time</HeaderCell>
                        <HeaderCell width={82}>Target</HeaderCell>
                        {!activePlan.compact && <HeaderCell width={72}>Tempo</HeaderCell>}
                        {!activePlan.compact && <HeaderCell width={92}>Pause</HeaderCell>}
                        {Array.from({ length: maxSets }).map((_, setIndex) => (
                          <HeaderCell key={setIndex} width={96} align="center">
                            Set {setIndex + 1}
                          </HeaderCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeDay.exercises.map((exercise) => (
                        <TableRow
                          key={exercise.id}
                          sx={{
                            "&:nth-of-type(even)": { bgcolor: "rgba(255, 255, 255, 0.025)" },
                            "&:hover": { bgcolor: "rgba(139, 92, 246, 0.08)" },
                          }}
                        >
                          {!activePlan.compact && (
                            <BodyCell sx={{ fontWeight: 800, color: "#c084fc" }}>
                              {exercise.label}
                            </BodyCell>
                          )}
                          <BodyCell sx={{ fontWeight: 700 }}>
                            {exercise.name}
                          </BodyCell>
                          <BodyCell>
                            <PreviousSessionSummary
                              session={previousExerciseSessions[exercise.id]}
                            />
                          </BodyCell>
                          <BodyCell>{exercise.targetReps}</BodyCell>
                          {!activePlan.compact && <BodyCell>{exercise.tempo ?? "-"}</BodyCell>}
                          {!activePlan.compact && <BodyCell>{exercise.pause}</BodyCell>}
                          {Array.from({ length: maxSets }).map((_, setIndex) => {
                            const setNumber = setIndex + 1;

                            if (setNumber > exercise.sets) {
                              return (
                                <BodyCell
                                  key={setNumber}
                                  sx={{ bgcolor: "rgba(255, 255, 255, 0.025)" }}
                                />
                              );
                            }

                            const repsKey = getSetKey(
                              weekId,
                              activeDay.id,
                              exercise.id,
                              setNumber,
                              "reps"
                            );
                            const weightKey = getSetKey(
                              weekId,
                              activeDay.id,
                              exercise.id,
                              setNumber,
                              "weight"
                            );

                            return (
                              <BodyCell key={repsKey} align="center">
                                <Stack spacing={0.65} sx={{ alignItems: "center" }}>
                                  <SetValueInput
                                    value={entries[weightKey]?.value ?? ""}
                                    state={saveStates[weightKey]}
                                    field="weight"
                                    placeholder="kg"
                                    label={`${exercise.name} set ${setNumber} weight`}
                                    onChange={(value) =>
                                      handleCellChange(weightKey, value, "weight")
                                    }
                                    width={74}
                                  />
                                  <SetValueInput
                                    value={entries[repsKey]?.value ?? ""}
                                    state={saveStates[repsKey]}
                                    field="reps"
                                    placeholder="reps"
                                    label={`${exercise.name} set ${setNumber} reps`}
                                    onChange={(value) =>
                                      handleCellChange(repsKey, value, "reps")
                                    }
                                    width={74}
                                  />
                                </Stack>
                              </BodyCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Box>

          <Paper elevation={0} sx={{ ...panelSx, p: 2, position: { xl: "sticky" }, top: 24 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", mb: 1.5, color: "white" }}>
              overview
            </Typography>

            <Stack spacing={1}>
              {trainingDays.map((day, index) => {
                const daySets = day.exercises.reduce(
                  (sum, exercise) => sum + exercise.sets,
                  0
                );
                const dayFilled = day.exercises.reduce((sum, exercise) => {
                  return (
                    sum +
                    Array.from({ length: exercise.sets }).filter((_, setIndex) => {
                      const key = getSetKey(weekId, day.id, exercise.id, setIndex + 1);
                      return entries[key]?.value !== undefined && entries[key].value !== "";
                    }).length
                  );
                }, 0);

                return (
                  <Button
                    key={day.id}
                    onClick={() => setActiveDayIndex(index)}
                    sx={{
                      justifyContent: "space-between",
                      borderRadius: 1,
                      border:
                        activeDayIndex === index
                          ? "1px solid rgba(168, 85, 247, 0.55)"
                          : "1px solid rgba(255, 255, 255, 0.1)",
                      bgcolor:
                        activeDayIndex === index
                          ? "rgba(139, 92, 246, 0.18)"
                          : "rgba(255, 255, 255, 0.04)",
                      color: "white",
                      px: 1.25,
                      py: 1,
                      "&:hover": { bgcolor: "rgba(139, 92, 246, 0.16)" },
                    }}
                  >
                    <Box sx={{ textAlign: "left", minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: "0.9rem" }}>
                        {day.title}
                      </Typography>
                      <Typography sx={{ color: "rgba(255, 255, 255, 0.55)", fontSize: "0.78rem" }}>
                        {day.exercises.length} exercises
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${dayFilled}/${daySets}`}
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.08)",
                        color: "white",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        fontWeight: 800,
                      }}
                    />
                  </Button>
                );
              })}
            </Stack>

            <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(255, 255, 255, 0.1)" }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "white", mb: 0.35 }}>
                equipment notes
              </Typography>
              <Typography sx={{ color: "rgba(255, 255, 255, 0.52)", fontSize: "0.76rem", mb: 1 }}>
                Global machine settings, seat heights and reminders.
              </Typography>
              <TextField
                value={globalNote}
                onChange={(event) => {
                  setGlobalNote(event.target.value);
                  setNoteSaveState(null);
                }}
                placeholder={noteLoading ? "Loading notes..." : "Hack squat: seat 4\nChest press: seat 6"}
                multiline
                minRows={5}
                fullWidth
                disabled={noteLoading}
                inputProps={{ maxLength: 10000, "aria-label": "Global equipment notes" }}
                sx={{
                  ...darkFieldSx,
                  "& textarea": { color: "white", fontSize: "0.88rem", lineHeight: 1.5 },
                }}
              />
              <Button
                onClick={saveGlobalNote}
                disabled={noteLoading || noteSaveState === "saving"}
                fullWidth
                sx={{
                  mt: 1,
                  color: "white",
                  bgcolor: "rgba(139, 92, 246, 0.22)",
                  border: "1px solid rgba(168, 85, 247, 0.42)",
                  "&:hover": { bgcolor: "rgba(139, 92, 246, 0.32)" },
                }}
              >
                {noteSaveState === "saving"
                  ? "saving..."
                  : noteSaveState === "saved"
                  ? "saved"
                  : noteSaveState === "error"
                  ? "retry save"
                  : "save note"}
              </Button>
            </Box>

            <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(255, 255, 255, 0.1)" }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
                <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 20 }} />
                <Typography sx={{ color: "rgba(255, 255, 255, 0.68)", fontSize: "0.9rem" }}>
                  {lastSync ? lastSync.toLocaleTimeString() : "not synced"}
                </Typography>
              </Stack>
              {Object.values(saveStates).includes("error") && (
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <ErrorOutlineIcon sx={{ color: "#f87171", fontSize: 20 }} />
                  <Typography sx={{ color: "#fecaca", fontSize: "0.9rem" }}>
                    save error
                  </Typography>
                </Stack>
              )}
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

interface TrainingShellProps {
  children: React.ReactNode;
}

const TrainingShell: React.FC<TrainingShellProps> = ({ children }) => (
  <Box
    sx={{
      minHeight: "100vh",
      background:
        "linear-gradient(135deg, #0f0f23 0%, #1a1a2e 25%, #16213e 50%, #1a1a2e 75%, #0f0f23 100%)",
      color: "white",
      py: { xs: 2, md: 4 },
    }}
  >
    <Container maxWidth="sm" sx={{ px: { xs: 1.5, sm: 2, md: 3 } }}>
      <Button
        component={Link}
        to="/"
        startIcon={<ArrowBackIcon />}
        sx={{
          color: "white",
          border: "1px solid rgba(99, 102, 241, 0.35)",
          bgcolor: "rgba(15, 23, 42, 0.35)",
          mb: { xs: 2, md: 3 },
          "&:hover": {
            bgcolor: "rgba(99, 102, 241, 0.12)",
            borderColor: "rgba(168, 85, 247, 0.65)",
          },
        }}
      >
        back
      </Button>
      {children}
    </Container>
  </Box>
);

const renderLoadingPanel = () => (
  <Paper
    elevation={0}
    sx={{
      ...panelSx,
      p: 4,
      display: "grid",
      placeItems: "center",
      minHeight: 220,
    }}
  >
    <CircularProgress sx={{ color: "#c084fc" }} />
  </Paper>
);

interface LoginPanelProps {
  username: string;
  password: string;
  loading: boolean;
  error: string | null;
  onUsernameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}

const LoginPanel: React.FC<LoginPanelProps> = ({
  username,
  password,
  loading,
  error,
  onUsernameChange,
  onPasswordChange,
  onSubmit,
}) => (
  <Paper
    component="form"
    onSubmit={onSubmit}
    elevation={0}
    sx={{
      ...panelSx,
      p: { xs: 2, sm: 3 },
    }}
  >
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
        <FitnessCenterIcon sx={{ color: "#c084fc" }} />
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "2rem", sm: "2.4rem" },
            lineHeight: 1.05,
            fontWeight: 800,
            background:
              "linear-gradient(45deg, #6366f1 20%, #8b5cf6 42%, #a855f7 68%, #c084fc 88%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: 0,
          }}
        >
          training
        </Typography>
      </Stack>

      <TextField
        value={username}
        onChange={(event) => onUsernameChange(event.target.value)}
        label="Username"
        autoComplete="username"
        fullWidth
        sx={darkFieldSx}
      />
      <TextField
        value={password}
        onChange={(event) => onPasswordChange(event.target.value)}
        label="Password"
        type="password"
        autoComplete="current-password"
        fullWidth
        sx={darkFieldSx}
      />

      {error && (
        <Alert severity="error" sx={{ borderRadius: 1 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        disabled={loading}
        sx={{
          background: "linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)",
          color: "white",
          py: 1.25,
          "&:hover": {
            background: "linear-gradient(45deg, #4f46e5 30%, #7c3aed 90%)",
          },
          "&.Mui-disabled": {
            color: "rgba(255, 255, 255, 0.55)",
          },
        }}
      >
        {loading ? "logging in" : "log in"}
      </Button>
    </Stack>
  </Paper>
);

interface ExerciseCardProps {
  exercise: TrainingExercise;
  compact: boolean;
  weekId: string;
  dayId: string;
  previousSession?: PreviousExerciseSession;
  entries: Record<string, Entry>;
  saveStates: Record<string, SaveState>;
  onCellChange: (
    cellKey: string,
    value: string,
    field: TrainingSetField
  ) => void;
}

const ExerciseCard: React.FC<ExerciseCardProps> = ({
  exercise,
  compact,
  weekId,
  dayId,
  previousSession,
  entries,
  saveStates,
  onCellChange,
}) => (
  <Box
    sx={{
      border: "1px solid rgba(255, 255, 255, 0.1)",
      bgcolor: "rgba(255, 255, 255, 0.045)",
      borderRadius: 1,
      p: 1.25,
    }}
  >
    <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", mb: 1 }}>
      {!compact && (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: "rgba(139, 92, 246, 0.2)",
            color: "#c084fc",
            display: "grid",
            placeItems: "center",
            fontWeight: 900,
            flex: "0 0 auto",
          }}
        >
          {exercise.label}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: "white", fontWeight: 800, fontSize: "0.98rem", lineHeight: 1.2 }}>
          {exercise.name}
        </Typography>
        <Typography sx={{ color: "rgba(255, 255, 255, 0.58)", fontSize: "0.78rem", mt: 0.35 }}>
          {compact
            ? `${exercise.sets} sets x ${exercise.targetReps} reps`
            : `${exercise.targetReps} / ${exercise.tempo ?? "-"} / ${exercise.pause}`}
        </Typography>
      </Box>
    </Stack>

    <Box sx={{ mb: 1.1 }}>
      <PreviousSessionSummary session={previousSession} />
    </Box>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${exercise.sets}, minmax(0, 1fr))`,
        gap: 0.75,
      }}
    >
      {Array.from({ length: exercise.sets }).map((_, setIndex) => {
        const setNumber = setIndex + 1;
        const repsKey = getSetKey(weekId, dayId, exercise.id, setNumber, "reps");
        const weightKey = getSetKey(
          weekId,
          dayId,
          exercise.id,
          setNumber,
          "weight"
        );

        return (
          <Box key={repsKey}>
            <Typography
              sx={{
                color: "rgba(255, 255, 255, 0.58)",
                fontSize: "0.72rem",
                fontWeight: 800,
                mb: 0.4,
              }}
            >
              Set {setNumber}
            </Typography>
            <Stack spacing={0.65}>
              <SetValueInput
                value={entries[weightKey]?.value ?? ""}
                state={saveStates[weightKey]}
                field="weight"
                placeholder="kg"
                label={`${exercise.name} set ${setNumber} weight`}
                onChange={(value) => onCellChange(weightKey, value, "weight")}
                width="100%"
              />
              <SetValueInput
                value={entries[repsKey]?.value ?? ""}
                state={saveStates[repsKey]}
                field="reps"
                placeholder="reps"
                label={`${exercise.name} set ${setNumber} reps`}
                onChange={(value) => onCellChange(repsKey, value, "reps")}
                width="100%"
              />
            </Stack>
          </Box>
        );
      })}
    </Box>
  </Box>
);

interface PreviousSessionSummaryProps {
  session?: PreviousExerciseSession;
}

const PreviousSessionSummary: React.FC<PreviousSessionSummaryProps> = ({
  session,
}) => {
  if (!session) {
    return (
      <Typography sx={{ color: "rgba(255, 255, 255, 0.42)", fontSize: "0.76rem" }}>
        no previous log
      </Typography>
    );
  }

  const setSummary = session.sets.map(formatPreviousSet).filter(Boolean).join(" / ");

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={0.65}
        sx={{ alignItems: "center", minWidth: 0, mb: 0.25 }}
      >
        <HistoryIcon sx={{ color: "#c084fc", fontSize: 16, flex: "0 0 auto" }} />
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.64)",
            fontSize: "0.74rem",
            fontWeight: 800,
            whiteSpace: "nowrap",
          }}
        >
          {formatSessionWeek(session.weekStart)}
        </Typography>
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.38)",
            fontSize: "0.72rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {session.dayTitle}
        </Typography>
      </Stack>
      <Typography
        sx={{
          color: "white",
          fontSize: "0.82rem",
          fontWeight: 800,
          lineHeight: 1.25,
          overflowWrap: "anywhere",
        }}
      >
        {setSummary || "saved without set values"}
      </Typography>
    </Box>
  );
};

interface SetValueInputProps {
  value: string;
  state?: SaveState;
  field: TrainingSetField;
  placeholder: string;
  label: string;
  onChange: (value: string) => void;
  width: number | string;
}

const SetValueInput: React.FC<SetValueInputProps> = ({
  value,
  state,
  field,
  placeholder,
  label,
  onChange,
  width,
}) => (
  <TextField
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    size="small"
    inputProps={{
      inputMode: field === "weight" ? "decimal" : "numeric",
      pattern: field === "weight" ? "[0-9]*[.,]?[0-9]*" : "[0-9]*",
      "aria-label": label,
    }}
    sx={{
      width,
      "& input": {
        color: "white",
        textAlign: "center",
        fontWeight: 900,
        p: "9px 6px",
      },
      "& .MuiOutlinedInput-root": {
        bgcolor: getInputBackground(state),
        borderRadius: 1,
        "& fieldset": {
          borderColor: getInputBorder(state),
        },
        "&:hover fieldset": {
          borderColor: getInputBorder(state, true),
        },
        "&.Mui-focused fieldset": {
          borderColor: "#c084fc",
          borderWidth: 2,
        },
      },
    }}
  />
);

interface CellProps {
  children?: React.ReactNode;
  width?: number;
  align?: "left" | "center" | "right";
  sx?: object;
}

const HeaderCell: React.FC<CellProps> = ({ children, width, align = "left" }) => (
  <TableCell
    align={align}
    sx={{
      width,
      color: "white",
      borderColor: "rgba(255, 255, 255, 0.1)",
      fontWeight: 800,
      whiteSpace: "nowrap",
      py: 1,
    }}
  >
    {children}
  </TableCell>
);

const BodyCell: React.FC<CellProps> = ({ children, align = "left", sx }) => (
  <TableCell
    align={align}
    sx={{
      color: "rgba(255, 255, 255, 0.86)",
      borderColor: "rgba(255, 255, 255, 0.08)",
      fontSize: "0.92rem",
      py: 0.85,
      ...sx,
    }}
  >
    {children}
  </TableCell>
);

const panelSx = {
  borderRadius: 2,
  border: "1px solid rgba(99, 102, 241, 0.22)",
  bgcolor: "rgba(15, 23, 42, 0.72)",
  backdropFilter: "blur(14px)",
  boxShadow: "0 24px 70px rgba(0, 0, 0, 0.28)",
  overflow: "hidden",
};

const iconButtonSx = {
  bgcolor: "rgba(255, 255, 255, 0.06)",
  border: "1px solid rgba(255, 255, 255, 0.12)",
  color: "white",
  "&:hover": { bgcolor: "rgba(139, 92, 246, 0.18)" },
  "&.Mui-disabled": { color: "rgba(255, 255, 255, 0.35)" },
};

const darkFieldSx = {
  "& input": {
    color: "white",
    fontWeight: 700,
  },
  "& .MuiOutlinedInput-root": {
    bgcolor: "rgba(255, 255, 255, 0.06)",
    borderRadius: 1,
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.14)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(192, 132, 252, 0.7)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#c084fc",
    },
  },
};

const sanitizeSetValue = (rawValue: string, field: TrainingSetField) => {
  if (field === "reps") {
    return rawValue.replace(/\D/g, "").slice(0, 3);
  }

  const normalized = rawValue.replace(",", ".").replace(/[^\d.]/g, "");
  const [whole = "", decimal = ""] = normalized.split(".");
  const wholePart = whole.slice(0, 4);
  const decimalPart = decimal.slice(0, 2);

  if (!normalized.includes(".")) {
    return wholePart;
  }

  return `${wholePart}.${decimalPart}`;
};

const buildPreviousExerciseSessions = (
  entries: Record<string, Entry>,
  currentWeekStart: Date,
  currentDayId: string,
  plan: TrainingPlan
): Record<string, PreviousExerciseSession> => {
  const dayOrder = new Map(plan.days.map((day, index) => [day.id, index]));
  const dayTitle = new Map(plan.days.map((day) => [day.id, day.title]));
  const currentDayOrder = dayOrder.get(currentDayId);

  if (currentDayOrder === undefined) {
    return {};
  }

  const currentOrder = getOccurrenceOrder(currentWeekStart, currentDayOrder);
  const sessions = new Map<
    string,
    {
      exerciseId: string;
      order: number;
      weekStart: Date;
      dayTitle: string;
      sets: Map<number, PreviousSetSnapshot>;
    }
  >();

  Object.entries(entries).forEach(([cellKey, entry]) => {
    const value = entry.value.trim();

    if (!value) {
      return;
    }

    const parsed = parseSetKey(cellKey);

    if (!parsed) {
      return;
    }

    const parsedDayOrder = dayOrder.get(parsed.dayId);

    if (parsedDayOrder === undefined) {
      return;
    }

    const weekStart = parseDateId(parsed.weekId);
    const order = getOccurrenceOrder(weekStart, parsedDayOrder);

    if (order >= currentOrder) {
      return;
    }

    const sessionKey = `${parsed.weekId}.${parsed.dayId}.${parsed.exerciseId}`;
    const session =
      sessions.get(sessionKey) ??
      {
        exerciseId: parsed.exerciseId,
        order,
        weekStart,
        dayTitle: dayTitle.get(parsed.dayId) ?? parsed.dayId,
        sets: new Map<number, PreviousSetSnapshot>(),
      };

    const set =
      session.sets.get(parsed.setNumber) ?? { setNumber: parsed.setNumber };

    if (parsed.field === "weight") {
      set.weight = value;
    } else {
      set.reps = value;
    }

    session.sets.set(parsed.setNumber, set);
    sessions.set(sessionKey, session);
  });

  const latestByExercise = new Map<
    string,
    {
      order: number;
      session: PreviousExerciseSession;
    }
  >();

  sessions.forEach((session) => {
    const sets = Array.from(session.sets.values()).sort(
      (first, second) => first.setNumber - second.setNumber
    );
    const previous = latestByExercise.get(session.exerciseId);

    if (!previous || session.order > previous.order) {
      latestByExercise.set(session.exerciseId, {
        order: session.order,
        session: {
          weekStart: session.weekStart,
          dayTitle: session.dayTitle,
          sets,
        },
      });
    }
  });

  const result: Record<string, PreviousExerciseSession> = {};

  latestByExercise.forEach((value, exerciseId) => {
    result[exerciseId] = value.session;
  });

  return result;
};

type ParsedSetKey = {
  weekId: string;
  dayId: string;
  exerciseId: string;
  setNumber: number;
  field: TrainingSetField;
};

const parseSetKey = (cellKey: string): ParsedSetKey | null => {
  const match =
    /^week-(\d{4}-\d{2}-\d{2})\.([^.]+)\.([^.]+)\.set-(\d+)(\.weight)?$/i.exec(
      cellKey
    );

  if (!match) {
    return null;
  }

  return {
    weekId: match[1],
    dayId: match[2],
    exerciseId: match[3],
    setNumber: Number(match[4]),
    field: match[5] ? "weight" : "reps",
  };
};

const getOccurrenceOrder = (weekStart: Date, dayIndex: number) =>
  weekStart.getTime() + dayIndex * oneDayMs;

const parseDateId = (dateId: string) => {
  const [year, month, day] = dateId.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatPreviousSet = (set: PreviousSetSnapshot) => {
  if (set.weight && set.reps) {
    return `${set.weight}kg x ${set.reps}`;
  }

  if (set.weight) {
    return `${set.weight}kg`;
  }

  if (set.reps) {
    return `${set.reps} reps`;
  }

  return "";
};

const formatSessionWeek = (date: Date) =>
  `week ${date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  })}`;

const getInputBackground = (state?: SaveState) => {
  if (state === "saving") {
    return "rgba(251, 191, 36, 0.12)";
  }

  if (state === "saved") {
    return "rgba(34, 197, 94, 0.12)";
  }

  if (state === "error") {
    return "rgba(248, 113, 113, 0.12)";
  }

  return "rgba(255, 255, 255, 0.07)";
};

const getInputBorder = (state?: SaveState, hover = false) => {
  if (state === "saving") {
    return "#fbbf24";
  }

  if (state === "saved") {
    return "#22c55e";
  }

  if (state === "error") {
    return "#f87171";
  }

  return hover ? "rgba(192, 132, 252, 0.78)" : "rgba(255, 255, 255, 0.16)";
};

const startOfWeek = (date: Date) => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = next.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  next.setDate(next.getDate() + mondayOffset);
  next.setHours(0, 0, 0, 0);
  return next;
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatDateId = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const weekLabel = (date: Date) => {
  const end = addDays(date, 6);
  return `${date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  })} - ${end.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  })}`;
};

const isoWeekNumber = (date: Date) => {
  const target = new Date(date.valueOf());
  const dayNumber = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNumber + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const firstDayNumber = (firstThursday.getDay() + 6) % 7;
  firstThursday.setDate(firstThursday.getDate() - firstDayNumber + 3);
  return 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * oneDayMs));
};

export default Training;
